import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Button from '../common/ui/Button';
import { getNovelImageUrl } from '../../utils/imageUtils';
import { getBestRankingDisplay } from '../../utils/rankingUtils';
import { toggleBookmark, getUserBookmarks } from '../../services/bookmarksApi';
import useViewerIdentity from '../../hooks/useViewerIdentity';
import './HeroSection.css';

const HeroSection = ({ novels = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timeoutRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const { user } = useViewerIdentity();
  const [bookmarkedNovels, setBookmarkedNovels] = useState({});
  const [togglingBookmark, setTogglingBookmark] = useState(false);

  // Fetch bookmark status for all hero novels in a single request when the
  // user is logged in. Uses getUserBookmarks (one round-trip returning the
  // user's full bookmark list) instead of N sequential getBookmarkStatus
  // calls (one per hero novel). When the user logs out (user?.id is null),
  // clear stale bookmark state so toggled indicators don't linger.
  useEffect(() => {
    if (!user?.id || !novels.length) {
      setBookmarkedNovels({});
      return;
    }
    let active = true;
    (async () => {
      try {
        const list = await getUserBookmarks(user.id);
        if (!active) return;
        const ids = new Set(
          (Array.isArray(list) ? list : [])
            .map((b) => b?.novelId)
            .filter(Boolean),
        );
        const statuses = {};
        for (const novel of novels) {
          statuses[novel.id] = ids.has(novel.id);
        }
        if (active) setBookmarkedNovels(statuses);
      } catch {
        if (active) setBookmarkedNovels({});
      }
    })();
    return () => { active = false; };
  }, [user?.id, novels]);

  const handleAddToLibrary = useCallback(async () => {
    const novel = novels[activeIndex];
    if (!user?.id || !novel?.id || togglingBookmark) return;
    setTogglingBookmark(true);
    try {
      const res = await toggleBookmark({ userId: user.id, novelId: novel.id });
      setBookmarkedNovels(prev => ({ ...prev, [novel.id]: Boolean(res?.bookmarked) }));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Failed to toggle bookmark', err);
      }
    } finally {
      setTogglingBookmark(false);
    }
  }, [user?.id, novels, activeIndex, togglingBookmark]);

  useEffect(() => {
    if (novels.length <= 1) return;
    const nextSlide = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % novels.length);
      }, 8000);
    };
    nextSlide();
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [activeIndex, novels.length]);

  const handleSlideChange = useCallback((index) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveIndex(index);
  }, []);

  const handlePrev = useCallback(() => {
    if (novels.length <= 1) return;
    handleSlideChange(activeIndex === 0 ? novels.length - 1 : activeIndex - 1);
  }, [activeIndex, novels.length, handleSlideChange]);

  const handleNext = useCallback(() => {
    if (novels.length <= 1) return;
    handleSlideChange((activeIndex + 1) % novels.length);
  }, [activeIndex, novels.length, handleSlideChange]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  }, [handleNext, handlePrev]);

  if (!novels || novels.length === 0) return null;

  const novel = novels[activeIndex];
  const rankInfo = getBestRankingDisplay(novel.rankings);
  const status = novel.status || (novel.isCompleted ? 'Completed' : 'Ongoing');
  const authorName = novel.author?.name || novel.authorName || 'Unknown';
  const synopsis = novel.description || novel.synopsis || '';
  const formatNum = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
  const genres = (novel.genres || []).slice(0, 4);

  // Only render backdrop images for the active slide and the next slide
  // (preload for smooth crossfade transition). Previously ALL slides' images
  // were rendered upfront — 5 full-size images loaded simultaneously even
  // though only 1 is visible. This cuts initial backdrop image count from
  // N to 2 (active + next), saving ~60-80% of hero image bandwidth on load.
  // When the user navigates, the new active slide was already preloaded as
  // "next", and the new "next" loads lazily. For jump navigation (clicking
  // a non-adjacent slide), the backdrop has a dark gradient overlay so a
  // brief image load is visually masked.
  const backdropIndices = new Set([activeIndex]);
  if (novels.length > 1) {
    backdropIndices.add((activeIndex + 1) % novels.length);
  }

  return (
    <div
      className="hero-section"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Ambient backdrop — cover art blurred with warm gradient.
          Uses <img> instead of background-image so the browser can
          prioritize the LCP image fetch (fetchpriority="high" on the
          active layer). background-image is fetched at low priority,
          which delays LCP by seconds on mobile.
          Only the active and next slide images are rendered — see
          backdropIndices above for the rationale. */}
      <div className="hero-section__backdrop">
        {novels.map((n, index) => backdropIndices.has(index) && (
          <img
            key={n.id}
            src={getNovelImageUrl(n)}
            alt=""
            className={`hero-section__backdrop-layer ${index === activeIndex ? 'is-active' : ''}`}
            width="800"
            height="600"
            {...(index === activeIndex
              ? { fetchpriority: 'high' }
              : { loading: 'lazy' })}
          />
        ))}
        <div className="hero-section__backdrop-overlay" />
      </div>

      <div className="hero-section__body">
        {/* Sharp cover image */}
        <div className="hero-section__cover">
          <img src={getNovelImageUrl(novel)} alt={novel.title} width="200" height="300" fetchpriority="high" />
        </div>

        {/* Text content */}
        <div className="hero-section__info">
          <div className="hero-section__badges">
            <span className="hero-chip">Featured</span>
            {rankInfo && (
              <span className={`hero-chip hero-chip--${rankInfo.cssModifier}`}>
                <span className="hero-chip__star">★</span>
                #{rankInfo.rankPosition} {rankInfo.label}
              </span>
            )}
            <span className="hero-chip">{status}</span>
          </div>

          <h2 className="hero-section__title">
            <Link to={`/novels/${novel.id}`}>{novel.title}</Link>
          </h2>

          <p className="hero-section__author">by {authorName}</p>

          {synopsis && (
            <p className="hero-section__synopsis">{synopsis}</p>
          )}

          <div className="hero-section__stats">
            {novel.averageRating != null && novel.averageRating > 0 && (
              <div className="hero-section__stat">
                <span className="hero-section__stat-value">★ {novel.averageRating.toFixed(1)}</span>
                <span className="hero-section__stat-label">Rating</span>
              </div>
            )}
            {novel.viewCount != null && (
              <div className="hero-section__stat">
                <span className="hero-section__stat-value">{formatNum(novel.viewCount)}</span>
                <span className="hero-section__stat-label">Views</span>
              </div>
            )}
            {novel.totalChapters != null && novel.totalChapters > 0 && (
              <div className="hero-section__stat">
                <span className="hero-section__stat-value">{novel.totalChapters}</span>
                <span className="hero-section__stat-label">Chapters</span>
              </div>
            )}
          </div>

          <div className="hero-section__actions hero-section__actions--dual">
            <Link to={`/novels/${novel.id}`}>
              <Button variant="primary" size="md" className="hero-btn-primary">
                Read Now
              </Button>
            </Link>
            {user ? (
              <Button
                variant="secondary"
                size="md"
                className="hero-btn-secondary"
                onClick={handleAddToLibrary}
                disabled={togglingBookmark}
              >
                {bookmarkedNovels[novel.id] ? '✓ In Library' : '+ Add to Library'}
              </Button>
            ) : (
              <Link to="/login">
                <Button variant="secondary" size="md" className="hero-btn-secondary">
                  + Add to Library
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Side picks — vertical strip on right (desktop only) */}
        {novels.length > 1 && (
          <div className="hero-section__side-picks">
            <span className="hero-section__side-picks-label">Featured</span>
            {novels.map((n, index) => (
              <button
                key={n.id}
                className={`hero-pick hero-pick--side ${index === activeIndex ? 'is-active' : ''}`}
                onClick={() => handleSlideChange(index)}
                aria-label={`Go to ${n.title}`}
                type="button"
              >
                <img src={getNovelImageUrl(n)} alt={n.title} width="44" height="66" loading={index === 0 ? 'eager' : 'lazy'} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail picks — horizontal strip at bottom (mobile only, hidden on desktop) */}
      {novels.length > 1 && (
        <div className="hero-section__picks">
          {novels.map((n, index) => (
            <button
              key={n.id}
              className={`hero-pick ${index === activeIndex ? 'is-active' : ''}`}
              onClick={() => handleSlideChange(index)}
              aria-label={`Go to ${n.title}`}
              type="button"
            >
              <img src={getNovelImageUrl(n)} alt={n.title} width="40" height="60" loading={index === 0 ? 'eager' : 'lazy'} />
            </button>
          ))}
        </div>
      )}

      {/* Compact dots — mobile only */}
      {novels.length > 1 && (
        <div className="hero-section__dots">
          {novels.map((_, index) => (
            <button
              key={index}
              className={`hero-dot ${index === activeIndex ? 'is-active' : ''}`}
              onClick={() => handleSlideChange(index)}
              aria-label={`Go to slide ${index + 1}`}
              type="button"
            />
          ))}
        </div>
      )}

      {/* Mobile arrow buttons */}
      {novels.length > 1 && (
        <>
          <button
            className="hero-section__nav hero-section__nav--prev"
            onClick={handlePrev}
            aria-label="Previous slide"
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            className="hero-section__nav hero-section__nav--next"
            onClick={handleNext}
            aria-label="Next slide"
            type="button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </>
      )}
    </div>
  );
};

export default HeroSection;
